// ─── Command Registry ─────────────────────────────────────────────────────────
// Central registry of all commands. Each command follows the shape:
//   { id, aliases, description, category, handler(args, context) }
//
// To add a command: append a new object to COMMANDS below.
// The help output is auto-generated from this registry.

import { projects } from '../data/portfolio';

/**
 * Build the command registry.
 * Receives `handlers` object so registry can stay pure (no circular deps).
 * handlers: { scrollTo, openProject, filterProjects, clearOutput, downloadResume }
 */
export function buildCommandRegistry(handlers) {
  // Dynamically generate open-project commands from project data
  const projectCommands = projects.map((p) => ({
    id: `open-${p.slug}`,
    aliases: [`open ${p.slug}`, `open ${p.title.toLowerCase()}`],
    description: `Open ${p.title} project`,
    category: 'projects',
    handler: () => handlers.openProject(p.id),
  }));

  const COMMANDS = [
    // ── Navigation ─────────────────────────────────────────
    {
      id: 'home',
      aliases: ['home', 'go home', 'scroll top'],
      description: 'Scroll to top',
      category: 'navigation',
      handler: () => handlers.scrollTo('hero'),
    },
    {
      id: 'about',
      aliases: ['about', 'about me', 'who are you'],
      description: 'Navigate to About section',
      category: 'navigation',
      handler: () => handlers.scrollTo('about'),
    },
    {
      id: 'projects',
      aliases: ['projects', 'show projects', 'list projects', 'my projects', 'show all projects'],
      description: 'Navigate to Projects section',
      category: 'navigation',
      handler: () => {
        handlers.filterProjects(null);
        handlers.scrollTo('projects');
      },
    },
    {
      id: 'experience',
      aliases: ['experience', 'show experience', 'show work experience', 'show career', 'work history'],
      description: 'Navigate to Experience section',
      category: 'navigation',
      handler: () => handlers.scrollTo('experience'),
    },
    {
      id: 'skills',
      aliases: ['skills', 'show skills', 'show tech stack', 'tech stack', 'stack'],
      description: 'Navigate to Skills section',
      category: 'navigation',
      handler: () => handlers.scrollTo('stack'),
    },
    {
      id: 'contact',
      aliases: ['contact', 'contact me', 'get in touch', 'hire me'],
      description: 'Navigate to Contact section',
      category: 'navigation',
      handler: () => handlers.scrollTo('contact'),
    },

    // ── Project Filters ────────────────────────────────────
    {
      id: 'filter-ai',
      aliases: ['show ai projects', 'ai projects', 'show ml projects', 'show machine learning'],
      description: 'Show AI / ML projects',
      category: 'projects',
      handler: () => {
        handlers.filterProjects('ai');
        handlers.scrollTo('projects');
      },
    },
    {
      id: 'filter-fullstack',
      aliases: ['show web projects', 'show fullstack projects', 'show full stack projects', 'show frontend projects'],
      description: 'Show Full Stack / Web projects',
      category: 'projects',
      handler: () => {
        handlers.filterProjects('fullstack');
        handlers.scrollTo('projects');
      },
    },
    {
      id: 'filter-backend',
      aliases: ['show backend projects', 'backend projects', 'show api projects'],
      description: 'Show Backend / API projects',
      category: 'projects',
      handler: () => {
        handlers.filterProjects('backend');
        handlers.scrollTo('projects');
      },
    },
    {
      id: 'filter-cloud',
      aliases: ['show cloud projects', 'cloud projects', 'show devops projects', 'show aws projects'],
      description: 'Show Cloud / DevOps projects',
      category: 'projects',
      handler: () => {
        handlers.filterProjects('cloud');
        handlers.scrollTo('projects');
      },
    },
    {
      id: 'filter-mobile',
      aliases: ['show mobile projects', 'mobile projects', 'show react native projects'],
      description: 'Show Mobile projects',
      category: 'projects',
      handler: () => {
        handlers.filterProjects('mobile');
        handlers.scrollTo('projects');
      },
    },

    // ── Project open commands (dynamic) ────────────────────
    ...projectCommands,

    // ── System ─────────────────────────────────────────────
    {
      id: 'resume',
      aliases: ['resume', 'download resume', 'get resume', 'cv'],
      description: 'Download resume',
      category: 'system',
      handler: () => handlers.downloadResume(),
    },
    {
      id: 'clear',
      aliases: ['clear', 'cls', 'reset'],
      description: 'Clear command output',
      category: 'system',
      handler: () => handlers.clearOutput(),
    },
    {
      id: 'help',
      aliases: ['help', '?', 'commands', 'list commands'],
      description: 'Show available commands',
      category: 'system',
      handler: (_, registry) => handlers.showHelp(registry),
    },
  ];

  return COMMANDS;
}

/**
 * Generate all valid alias strings from the registry (for autocomplete + parsing).
 */
export function getAllAliases(registry) {
  const aliases = [];
  for (const cmd of registry) {
    for (const alias of cmd.aliases) {
      aliases.push({ alias, command: cmd });
    }
  }
  return aliases;
}

/**
 * Generate formatted help text from the registry.
 */
export function generateHelp(registry) {
  const categories = {
    navigation: [],
    projects: [],
    system: [],
  };

  for (const cmd of registry) {
    // skip dynamic open-* entries for help — list them under a generic entry
    if (cmd.id.startsWith('open-')) continue;
    const cat = cmd.category || 'system';
    if (categories[cat]) {
      categories[cat].push(`  ${cmd.aliases[0].padEnd(28)} ${cmd.description}`);
    }
  }

  // Add generic open entry
  categories.projects.push(`  open <project-slug>           Open a specific project`);
  categories.projects.push(`  Slugs: ${projects.map((p) => p.slug).join(', ')}`);

  return [
    'AVAILABLE COMMANDS',
    '─'.repeat(48),
    '',
    'NAVIGATION',
    ...categories.navigation,
    '',
    'PROJECTS',
    ...categories.projects,
    '',
    'SYSTEM',
    ...categories.system,
    '',
    'TIP  Use ↑ ↓ to navigate history · Tab to autocomplete',
  ].join('\n');
}
